import { useState } from 'react';
import {
  MantineProvider,
  ColorSchemeProvider,
  ColorScheme,
} from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { AppRoutes } from './routes';
import { AuthProvider } from './contexts/AuthContext';
import { showError } from './lib/notifications';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      onError: (error) => {
        showError(error);
      },
    },
    mutations: {
      onError: (error) => {
        showError(error);
      },
    },
  },
});

const getStoredScheme = (): ColorScheme => {
  const stored = localStorage.getItem('themeMode');
  return stored === 'light' || stored === 'dark' ? stored : 'dark';
};

function App() {
  const [colorScheme, setColorScheme] = useState<ColorScheme>(getStoredScheme);

  const toggleColorScheme = (value?: ColorScheme) => {
    const next = value || (colorScheme === 'dark' ? 'light' : 'dark');
    setColorScheme(next);
    localStorage.setItem('themeMode', next);
  };

  return (
    <QueryClientProvider client={queryClient}>
      <ColorSchemeProvider colorScheme={colorScheme} toggleColorScheme={toggleColorScheme}>
        <MantineProvider
          withGlobalStyles
          withNormalizeCSS
          theme={{
            colorScheme,
            primaryColor: 'thanos',
            primaryShade: 5,
            colors: {
              // Databricks-style red-orange (primary #FF3621)
              thanos: [
                '#ffe9e5', // 0
                '#ffd0c7', // 1
                '#ffb0a1', // 2
                '#ff8e78', // 3
                '#ff6b4a', // 4 - secondary (dark mode)
                '#ff3621', // 5 - primary main
                '#e62d19', // 6
                '#d42e1b', // 7 - secondary (light mode)
                '#b02414', // 8
                '#8f1c0f', // 9
              ],
              // Legacy alias so existing `smallgroup` references stay on-brand
              smallgroup: [
                '#ffe9e5',
                '#ffd0c7',
                '#ffb0a1',
                '#ff8e78',
                '#ff6b4a',
                '#ff3621',
                '#e62d19',
                '#d42e1b',
                '#b02414',
                '#8f1c0f',
              ],
              // Dark surfaces matching thanos (paper #242424 / body #1B1B1B)
              dark: [
                '#e0e0e0', // 0 - text primary
                '#c8c8c8', // 1
                '#a0a0a0', // 2 - text secondary
                '#6b6b6b', // 3 - dimmed
                '#333333', // 4 - borders / divider
                '#2c2c2c', // 5 - hover surface
                '#242424', // 6 - paper / component bg
                '#1b1b1b', // 7 - body background
                '#161616', // 8
                '#111111', // 9
              ],
            },
            fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            headings: {
              fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
              fontWeight: 600,
            },
            defaultRadius: 'md',
            components: {
              // Tab lists often scroll horizontally, which clips an outside
              // focus ring into two brackets; draw it inside the tab instead.
              Tabs: {
                styles: () => ({
                  tab: { '&:focus-visible': { outlineOffset: -2 } },
                }),
              },
              Button: {
                defaultProps: {
                  size: 'sm',
                },
                styles: () => ({
                  root: {
                    fontWeight: 500,
                    borderRadius: 8,
                    textTransform: 'none',
                  },
                }),
              },
              Paper: {
                styles: (theme) => ({
                  root: {
                    borderRadius: 12,
                    border: `1px solid ${
                      theme.colorScheme === 'dark' ? '#333333' : '#e0e0e0'
                    }`,
                  },
                }),
              },
              Card: {
                styles: (theme) => ({
                  root: {
                    borderRadius: 12,
                    border: `1px solid ${
                      theme.colorScheme === 'dark' ? '#333333' : '#e0e0e0'
                    }`,
                    boxShadow:
                      theme.colorScheme === 'dark'
                        ? '0 4px 12px rgba(0, 0, 0, 0.4)'
                        : '0 4px 12px rgba(0, 0, 0, 0.08)',
                  },
                }),
              },
              Modal: {
                styles: () => ({
                  content: { borderRadius: 16 },
                  modal: { borderRadius: 16 },
                }),
              },
              Tooltip: {
                defaultProps: {
                  withArrow: false,
                  openDelay: 200,
                  transitionProps: { transition: 'fade', duration: 150 },
                },
                styles: () => ({
                  tooltip: {
                    backgroundColor: 'rgba(60, 60, 60, 0.96)',
                    color: '#ffffff',
                    fontSize: 12.5,
                    fontWeight: 500,
                    padding: '6px 10px',
                    borderRadius: 6,
                  },
                }),
              },
              TextInput: {
                styles: () => ({
                  input: { borderRadius: 8 },
                }),
              },
              PasswordInput: {
                styles: () => ({
                  input: { borderRadius: 8 },
                }),
              },
            },
          }}
        >
          <Notifications position="top-right" />
          <BrowserRouter>
            <AuthProvider>
              <AppRoutes />
            </AuthProvider>
          </BrowserRouter>
        </MantineProvider>
      </ColorSchemeProvider>
    </QueryClientProvider>
  );
}

export default App;
